<script lang="ts">
const modalName = 'common:files:add-file-modal'
</script>
<script setup lang="ts">
    import type { FileFilter } from '~/components/partials/Form/FileUploadDropZone.vue';
    import type { ImageConstraints } from '~~/utils/imageConstraints';
    import type { ImageValidationFailure } from '~/composables/useImageValidation';

    interface AddFileModalPayload {
        files?: FileFilter;
        title?: string;
        skipLabel?: string | null;
        acceptLabel?: string;
        multiple?: boolean;
        // When set, selected files are validated against these image constraints before emitting.
        // Absent → the modal behaves exactly as before (opt-in, non-breaking for other callers).
        imageConstraints?: ImageConstraints;
    }

    const defaultFiles: FileFilter = { type: 'include', extensions: ['pdf'] };

    
    const { validateImages, buildConstraintHint, formatBatchFailures } = useImageValidation();

    const payload = ref<AddFileModalPayload>({
        files: defaultFiles,
        title: undefined,
        skipLabel: null,
        acceptLabel: undefined,
        multiple: false,
        imageConstraints: undefined,
    });
    const validationFailures = ref<ImageValidationFailure[]>([]);
    const validationBanner = computed(() => (validationFailures.value.length ? formatBatchFailures(validationFailures.value) : null));

    const handleFilesSelected = async (selectedFiles: File[]) => {
        const constraints = payload.value.imageConstraints;
        if (constraints) {
            validationFailures.value = [];
            const { failures } = await validateImages(selectedFiles, constraints);
            if (failures.length) {
                // Any invalid file blocks the whole batch: list every failing file, emit nothing (spec §4.6).
                validationFailures.value = failures;
                return;
            }
        }
        useEvent('common:files:add-file-modal:files-selected', selectedFiles);
        close();
    };

    const skip = () => {
        useEvent('common:files:add-file-modal:skip');
        close();
    };

    const resetState = () => {
        payload.value = {
            files: defaultFiles,
            title: undefined,
            skipLabel: null,
            acceptLabel: undefined,
            multiple: false,
            imageConstraints: undefined,
        };
        validationFailures.value = [];
    };

    const close = () => {
        resetState();
        useEvent('modal:close', modalName);
    };

    useListen('common:files:add-file-modal:open', (modalPayload: AddFileModalPayload | void) => {
        validationFailures.value = [];
        if (modalPayload) {
            payload.value = {
                files: modalPayload.files || defaultFiles,
                title: modalPayload.title,
                skipLabel: modalPayload.skipLabel || null,
                acceptLabel: modalPayload.acceptLabel,
                multiple: modalPayload.multiple || false,
                imageConstraints: modalPayload.imageConstraints,
            };
        }
        useEvent('modal:open', modalName);
    });

    useListen('common:files:add-file-modal:close', close);
</script>

<template>
    <Modal
        :name="modalName"
        :width="500"
        @before-close="resetState"
    >
        <div :class="$theme('modal.padding')">
            <div class="mt-4">
                <h2 :class="$theme('modal.titleLeft.bigText', 'max-w-[430px]')">
                    {{ payload.title ? $t(payload.title) : $t('invoicing.expense.add_file_modal.title') }}
                </h2>
            </div>

            <div class="mt-6 flex flex-col gap-4">
                <FormImageUploadErrorBanner
                    v-if="validationBanner"
                    :title="validationBanner.title"
                    :lines="validationBanner.lines"
                />
                <FormFileUploadDropZone
                    :files="payload.files || defaultFiles"
                    :title="payload.title"
                    :accept-label="payload.acceptLabel"
                    :hint="payload.imageConstraints ? buildConstraintHint(payload.imageConstraints) : undefined"
                    :multiple="payload.multiple"
                    @files-selected="handleFilesSelected"
                />
                <UiAction
                    v-if="payload.skipLabel"
                    :content="{
                        label: payload.skipLabel,

                        type: 'light',
                    }"
                    modifier="full"
                    @click="skip"
                />
            </div>
        </div>
    </Modal>
</template>
